/**
 * Store d'authentification Zustand
 *
 * ARCHITECTURE SIMPLIFIÉE (Janvier 2026):
 * - User data uniquement dans ce store
 * - Access token: en mémoire via tokenManager (pas ici)
 * - Refresh token: cookie HttpOnly (invisible au JS)
 *
 * Flux après F5:
 * 1. App.tsx appelle /refresh (cookie envoyé automatiquement)
 * 2. Backend retourne access token + user data
 * 3. setAccessToken() stocke le token en mémoire
 * 4. setUser() met à jour ce store
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { clearSession } from '../services/tokenManager';
import { UserWithTenant } from '../types/models';

interface AuthState {
  user: UserWithTenant | null;
  isAuthenticated: boolean;
  needsCompany: boolean;

  // Actions
  setUser: (user: UserWithTenant | null) => void;
  setNeedsCompany: (needs: boolean) => void;
  login: (user: UserWithTenant) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      user: null,
      isAuthenticated: false,
      needsCompany: false,

      setUser: (user) => set({
        user,
        isAuthenticated: !!user,
      }),

      setNeedsCompany: (needs) => set({ needsCompany: needs }),

      login: (user) => {
        set({
          user,
          isAuthenticated: true,
          needsCompany: !user.companyId,
        });
      },

      logout: () => {
        // Nettoyer le token en mémoire et les anciennes clés sessionStorage
        clearSession();

        set({
          user: null,
          isAuthenticated: false,
          needsCompany: false,
        });
      },
    }),
    { name: 'AuthStore' }
  )
);
